import {
  Box,
  Button,
  DialogActions,
  MenuItem,
  Select,
} from "@material-ui/core";
import { Dialog } from "legacy-components/dialog/Dialog";
import Typography from "legacy-components/typography/Typography";
import { InputContainer } from "modus/components/form/input-container/InputContainer";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import React, { useCallback, useState, useEffect, useRef } from "react";
import { useAsyncAction } from "modus/components/form/async-actions/AsyncAction";
import ApplicationService from "pages/home/applications/components/v2.0/ApplicationService";
import { IApplicationThrottling } from "./ApplicationThrottlingdetails";
import { resolveAPIErrorMessage } from "modus/components/error-message/ErrorMessage";
import Alert from "@material-ui/lab/Alert";

interface IApplicationThrottlingDialogProps {
  deploymentId: string;
  toggle: () => void;
  open: boolean;
  consumerKeyAppNameMap?: Record<string, string>;
  buttonType?: string;
  refresh?: () => void;
  consumerKey?: string;
  applicationName?: string;
  transactionLimit?: number;
  timeUnit?: string;
  initilalValues?: IApplicationThrottling | null;
  useApplicationThrottlingV2?: boolean;
}
export type TimeUnit = {
  key: string;
  value: string;
};

const availableTimeUnit: readonly TimeUnit[] = [
  { key: "per Second", value: "SECOND" },
  { key: "per Minute", value: "MINUTE" },
] as const;

export const ApplicationThrottlingDialog: React.FC<
  IApplicationThrottlingDialogProps
> = ({
  deploymentId,
  open,
  toggle,
  consumerKeyAppNameMap,
  buttonType = "Submit",
  refresh,
  initilalValues,
  useApplicationThrottlingV2 = false,
}) => {
  const getNormalizedTimeUnit = (unit?: string) => {
    if (!unit) return "";
    // API returns "ps", UI expects "SECOND"
    if (unit === "ps" || unit === "SECOND") return "SECOND";
    // API returns "pm", UI expects "MINUTE"
    if (unit === "pm" || unit === "MINUTE") return "MINUTE";
    return "";
  };

  const [hasFormError, setHasFormError] = useState<boolean>(false);
  const [consumerKey, setConsumerKey] = useState<string>(
    initilalValues?.consumerKey ?? "",
  );
  const [applicationName, setApplicationName] = useState<string>(
    initilalValues?.applicationName ?? "",
  );
  const [apiTransactionLimit, setApiTransactionLimit] = useState<string>(
    initilalValues?.spikeArrestLimit?.toString() ?? "",
  );
  const [apiTransactionTimeUnit, setApiTransactionTimeUnit] = useState<string>(
    getNormalizedTimeUnit(initilalValues?.spikeArrestTimeunit),
  );
  const isSubmittedRef = useRef(false);
  const { state, subscribe } = useAsyncAction(() =>
    useApplicationThrottlingV2
      ? ApplicationService.createApplicationThrottlingV2(deploymentId, consumerKey, {
          spikeArrestLimit: parseInt(apiTransactionLimit),
          spikeArrestTimeunit: apiTransactionTimeUnit,
        })
      : ApplicationService.createApplicationThrottling(deploymentId, consumerKey, {
          spikeArrestLimit: parseInt(apiTransactionLimit),
          spikeArrestTimeunit: apiTransactionTimeUnit,
        }),
  );

  useEffect(() => {
    if (isSubmittedRef.current && state.loaded) {
      refresh?.();
      toggle();
      isSubmittedRef.current = false;
    }
  }, [state.loaded, refresh, toggle]);

  const resetForm = useCallback(() => {
    setApiTransactionLimit("");
    setApiTransactionTimeUnit("");
    setConsumerKey("");
    setApplicationName("");
  }, [
    setApiTransactionLimit,
    setApiTransactionTimeUnit,
    setConsumerKey,
    setApplicationName,
  ]);

  const handleClose = useCallback(() => {
    resetForm();
    setHasFormError(false);
    setApiTransactionTimeUnit("");
    toggle();
  }, [resetForm, toggle, setHasFormError, setApiTransactionTimeUnit]);

  const handleSubmit = useCallback(() => {
    if (!subscribe || !toggle) {
      return;
    }
    isSubmittedRef.current = true;
    subscribe();
  }, [subscribe, toggle]);

  return (
    <>
      <Dialog
        open={open}
        title={"Add Client based Rate Limiting"}
        onClose={handleClose}
      >
        <Box>
          {state?.error && (
            <Alert severity="error" style={{ width: "100%" }}>
              {resolveAPIErrorMessage(state.error)}
            </Alert>
          )}
          <Box>
            <Typography type="body">
              Subscribed Application Name
              <span className="required">*</span>
            </Typography>
            <Select
              id="application-throttling-consumer-key-select"
              IconComponent={ExpandMoreIcon}
              variant="outlined"
              className="header-selector resource-version-select"
              placeholder="Select a subscribed application to which you need to limit the hit"
              style={{ minWidth: "100%" }}
              disabled={!!initilalValues || Object.keys(consumerKeyAppNameMap || {}).length == 0}
              renderValue={(selected) => {
                const selectedKey = selected as string;

                if (
                  consumerKeyAppNameMap &&
                  consumerKeyAppNameMap[selectedKey]
                ) {
                  return consumerKeyAppNameMap[selectedKey];
                }

                // Priority 2: Check the initial value (Edit Mode)
                if (
                  initilalValues &&
                  selectedKey === initilalValues.consumerKey
                ) {
                  return initilalValues.consumerKey;
                }

                return selectedKey;
              }}
              onChange={(event) => {
                const value = event.target.value as string;
                setConsumerKey(value);
                setApplicationName(consumerKeyAppNameMap?.[value] ?? "");

                if (value) {
                  setHasFormError(false);
                }
              }}
              value={consumerKey}
            >
              {consumerKeyAppNameMap &&
                Object.entries(consumerKeyAppNameMap).map(([key, name]) => {
                  if (key === initilalValues?.consumerKey) return null;
                  return (
                    
                    <MenuItem value={key} key={key} divider>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <Typography type="body">
                          <span style={{ fontWeight: 600 }}>{name}</span>
                        </Typography>
                        <span style={{ color: "gray", fontSize: "0.85rem" }}>
                          {key}
                        </span>
                      </div>
                    </MenuItem>
                  );
                })}
            </Select>
          </Box>
          <Box mt={3}>
            <Typography type="body">
              Api Transaction limit
              <span className="required">*</span>
            </Typography>
            <InputContainer
              variant="standard"
              autoFocus
              margin="dense"
              placeholder="Number of API hits allowed for the application"
              type="text"
              isOnTypeValidationEnabled={true}
              value={apiTransactionLimit}
              validate={{
                required: true,
                pattern: "^\\d+$",
                min: 1,
              }}
              setError={setHasFormError}
              onChange={(event) => setApiTransactionLimit(event.target.value)}
            ></InputContainer>
          </Box>
          <Box mt={3}>
            <Typography type="body">
              Time
              <span className="required">*</span>
            </Typography>
            <Select
              id="apiTransactionTimeUnit"
              IconComponent={ExpandMoreIcon}
              variant="outlined"
              className="header-selector resource-version-select"
              placeholder="API Transaction Time unit"
              style={{ minWidth: "100%" }}
              onChange={(event) => {
                const value = event.target.value as string;
                setApiTransactionTimeUnit(value);
                if (value) {
                  setHasFormError(false);
                }
              }}
              value={apiTransactionTimeUnit}
            >
              {availableTimeUnit?.map((unit) => {
                return (
                  <MenuItem value={unit.value} key={unit.value}>
                    {unit.key}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>
        </Box>

        <DialogActions style={{ marginTop: "20px" }}>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            onClick={toggle}
            disabled={state.loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={
              state.loading ||
              !consumerKey ||
              !applicationName ||
              !apiTransactionLimit ||
              !apiTransactionTimeUnit ||
              hasFormError
            }
          >
            {buttonType ?? "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
